/**
 * @module game/GameLoop
 * Drives the requestAnimationFrame render loop at ~60 fps, independent
 * of the MediaPipe tracking rate (~25–35 fps).
 *
 * Pointer positions are interpolated between tracking updates so blade
 * movement appears smooth even when tracking frames arrive at lower
 * frequency than render frames.
 */

/** Callback fired every render frame. */
export type FrameCallback = (deltaMs: number, totalMs: number) => void;

/** Lightweight rAF wrapper; start/stop as the game state changes. */
export class GameLoop {
  private _rafId: number | null = null;
  private _lastTime: number | null = null;
  private readonly _callback: FrameCallback;

  constructor(callback: FrameCallback) {
    this._callback = callback;
  }

  /** Starts the loop. Safe to call if already running (no-op). */
  start(): void {
    if (this._rafId !== null) return;
    this._rafId = requestAnimationFrame(this._tick);
  }

  /** Stops the loop and clears the pending frame. */
  stop(): void {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this._lastTime = null;
  }

  /** Whether the loop is currently running. */
  get running(): boolean {
    return this._rafId !== null;
  }

  private readonly _tick = (now: number): void => {
    const delta = this._lastTime === null ? 0 : now - this._lastTime;
    this._lastTime = now;

    this._callback(delta, now);

    // Re-schedule only if stop() hasn't been called during the callback.
    if (this._rafId !== null) {
      this._rafId = requestAnimationFrame(this._tick);
    }
  };
}
